/**
 * Variant-aware desktop gallery: show ONLY the selected color's images.
 *
 * Reads three inline maps rendered by pdp-gallery-grid.liquid:
 *   data-pdp-color-media      { "<color value>": ["gid://.../MediaImage/..", ...] }  (display order)
 *   data-pdp-variant-options  { "<variantId>": ["<opt1>", "<opt2>", ...] }
 *   data-pdp-variant-media    { "<variantId>": <featuredMediaId|null> }              (fallback hero)
 *
 * On the selected variant, we find which of its option values is a key in the
 * color map, then show/reorder that color's images (first = hero, odd trailing =
 * full-width). If the product has no color map, or the color isn't grouped, we
 * fall back to showing ALL images with the variant's featured image as hero —
 * so ungrouped products keep working exactly as before.
 *
 * Detection is 200ms polling (this theme's variant picker mutates the id input
 * via .value with no change event). Debug logs when window.__PDP_GALLERY_DEBUG.
 */
(function () {
  var POLL_MS = 200;
  if (typeof window.__PDP_GALLERY_DEBUG === "undefined") window.__PDP_GALLERY_DEBUG = false;
  function log() {
    if (window.__PDP_GALLERY_DEBUG) console.log.apply(console, ["[pdp-gallery]"].concat([].slice.call(arguments)));
  }
  function numId(v) { return String(v == null ? "" : v).split("/").pop(); } // GID or number -> numeric string

  // FIX (jul 3): the old slick gallery was replaced by the grid, but the theme's
  // VariantRadios.updateMedia still calls slick("slickGoTo", ...) and throws.
  // That throw aborts onVariantChange BEFORE updateVariantInput() runs, so the
  // hidden input[name="id"] never updates and this script never sees the color
  // change (price/URL freeze too). When the new grid is on the page, wrap
  // updateMedia so the leftover slick call can't kill the rest of the chain.
  ["variant-radios", "variant-selects"].forEach(function (name) {
    customElements.whenDefined(name).then(function () {
      if (!document.querySelector("[data-pdp-grid]")) return; // pages w/ old gallery: untouched
      var El = customElements.get(name);
      var orig = El && El.prototype.updateMedia;
      if (!orig) return;
      El.prototype.updateMedia = function () {
        try { orig.apply(this, arguments); }
        catch (e) { log("updateMedia suppressed (old slick gallery removed):", e.message); }
      };
    });
  });

  function parseJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent || "null"); } catch (e) { log("JSON parse fail", e); return null; }
  }

  function init() {
    var grids = document.querySelectorAll("[data-pdp-grid]");
    log("init:", grids.length, "grid(s)");
    grids.forEach(setUpGrid);
    guardColumnOverflow();
    sortSizeButtons();
  }

  // FIX (jul 6): size buttons render in raw option-value order (Medium, Large,
  // XL, XXL, X-Small, Small, XXXL...). Sort them once at render: letter sizes
  // XXS -> XXXL, then numeric sizes ascending (handles waist-x-inseam like
  // 31x32 and eyewear like 52/20). Unknown labels keep their original order
  // at the end. Checked state and handlers survive the DOM move.
  function sortSizeButtons() {
    var RANK = {
      "xxs": 1, "2xs": 1, "xx-small": 1,
      "xs": 2, "x-small": 2,
      "s": 3, "small": 3,
      "m": 4, "medium": 4,
      "l": 5, "large": 5,
      "xl": 6, "x-large": 6,
      "xxl": 7, "xx-large": 7, "2xl": 7,
      "xxxl": 8, "xxx-large": 8, "3xl": 8,
      "one size": 50, "os": 50, "o/s": 50
    };
    document.querySelectorAll("fieldset.product-form__swatch").forEach(function (fs) {
      var legend = fs.querySelector("legend");
      if (!legend || !/size/i.test(legend.textContent)) return;
      var items = [].slice.call(fs.querySelectorAll(".product-form__swatch__item"));
      if (items.length < 2) return;
      var parent = items[0].parentElement;
      function key(item) {
        var input = item.querySelector("input");
        var v = ((input && input.value) || item.textContent).trim().toLowerCase();
        var norm = v.replace(/\s*-\s*/g, "-").replace(/\s+/g, " ");
        if (RANK[norm] != null) return [0, RANK[norm], 0];
        var m = norm.match(/^(\d+(?:\.\d+)?)(?:\s*[x\/]\s*(\d+(?:\.\d+)?))?/);
        if (m) return [1, parseFloat(m[1]), m[2] ? parseFloat(m[2]) : 0];
        return [2, 0, 0];
      }
      var keyed = items.map(function (it, i) { return { it: it, k: key(it), i: i }; });
      keyed.sort(function (a, b) {
        for (var j = 0; j < 3; j++) { if (a.k[j] !== b.k[j]) return a.k[j] - b.k[j]; }
        return a.i - b.i;
      });
      keyed.forEach(function (e) { parent.appendChild(e.it); });
    });
  }

  // FIX (jul 3, r3): .product-single__primary-blocks is position:absolute, so
  // the buy column contributes NO height — the section is sized by the gallery
  // alone. Once the color filter shrinks the gallery below the column height,
  // the column spills over the rails. Keep the content container at least as
  // tall as the column, and track both sides with ResizeObservers.
  function guardColumnOverflow() {
    var content = document.querySelector(".product-single__content");
    var col = document.querySelector(".product-single__primary-blocks");
    if (!content || !col || !("ResizeObserver" in window)) return;
    var inner = col.querySelector("product-details-sticky") || col;
    function sync() {
      if (window.matchMedia("(min-width: 981px)").matches &&
          getComputedStyle(col).position === "absolute") {
        content.style.minHeight = (inner.offsetHeight + 24) + "px";
      } else {
        content.style.minHeight = "";
      }
    }
    var ro = new ResizeObserver(sync);
    ro.observe(inner);
    window.addEventListener("resize", sync);
    sync();
  }

  function setUpGrid(grid) {
    // FIX (jul 3): Liquid pre-computes .pdp-grid--orphan from the TOTAL image
    // count, but its :last-of-type full-width rule fights the color filter
    // (a 2-up pair renders as half + full width). Once this script owns the
    // layout, gcell-hero/gcell-tail handle spans — drop the static class.
    grid.classList.remove("pdp-grid--orphan");
    var colorMedia = parseJSON(grid.querySelector("[data-pdp-color-media]")) || {};
    var variantOptions = parseJSON(grid.querySelector("[data-pdp-variant-options]")) || {};
    var variantMedia = parseJSON(grid.querySelector("[data-pdp-variant-media]")) || {};
    var hasColorMap = colorMedia && Object.keys(colorMedia).length > 0;
    log("color map colors:", Object.keys(colorMedia));

    var form = document.querySelector("form.product-form")
            || document.querySelector("form.js-product-form")
            || document.querySelector('form[action*="/cart/add"]');
    if (!form) { log("FAIL: no product form"); return; }

    function cells() { return [].slice.call(grid.querySelectorAll(".pdp-grid__cell")); }
    function byId() { var m = {}; cells().forEach(function (c) { m[c.dataset.mediaId] = c; }); return m; }

    // The option value on this variant that the color map knows about.
    function colorForVariant(vid) {
      var opts = variantOptions[vid] || [];
      for (var i = 0; i < opts.length; i++) { if (colorMedia[opts[i]]) return opts[i]; }
      return null;
    }

    function applyVariant(vid) {
      var map = byId();
      var order = null;
      var color = colorForVariant(vid);
      if (hasColorMap && color && colorMedia[color] && colorMedia[color].length) {
        order = colorMedia[color].map(numId).filter(function (id) { return map[id]; });
      }
      var all = cells();
      if (!order || !order.length) {
        // Fallback: show everything, featured image first.
        all.forEach(function (c) { c.classList.remove("gcell-hidden"); });
        var feat = variantMedia[vid] != null ? numId(variantMedia[vid]) : null;
        order = all.map(function (c) { return c.dataset.mediaId; });
        if (feat && map[feat]) order = [feat].concat(order.filter(function (id) { return id !== feat; }));
        log("variant", vid, "-> fallback (all", order.length, "images)");
      } else {
        // Filtered: hide anything not in this color's set.
        all.forEach(function (c) { c.classList.toggle("gcell-hidden", order.indexOf(c.dataset.mediaId) === -1); });
        log("variant", vid, "color", color, "-> show", order.length, "images");
      }
      // Reorder shown cells into `order` (each append moves to end, so final DOM order = order).
      order.forEach(function (id) { if (map[id]) grid.appendChild(map[id]); });
      // Hero + trailing-orphan full-width spans.
      all.forEach(function (c) { c.classList.remove("gcell-hero", "gcell-tail"); });
      if (order.length) {
        map[order[0]].classList.add("gcell-hero");
        var rest = order.length - 1;
        if (rest > 0 && rest % 2 === 1) map[order[order.length - 1]].classList.add("gcell-tail");
      }
      syncRail(color);
      // FIX (jul 3): filtering changes the gallery height; product-details-sticky
      // caches its max displacement and will slide the buy column over the rails
      // if we don't tell it to re-measure.
      window.dispatchEvent(new Event("resize"));
    }

    // FEATURE (jul 3): sold-out colors stay selectable (the back-in-stock flow
    // needs that), but say so plainly — append "— sold out" after the color
    // name whenever the selected swatch is fully out of stock (its label
    // carries .is-disabled, the same state that draws the strike-through).
    function updateSoldOutTags() {
      document.querySelectorAll("fieldset.product-form__swatch").forEach(function (fs) {
        var checked = fs.querySelector('input[type="radio"]:checked');
        var title = fs.querySelector(".js-option-title");
        if (!checked || !title) return;
        var fully = !!checked.closest(".product-form__swatch__item--sold-out");
        var tag = fs.querySelector(".pdp-color-soldout-tag");
        if (fully && !tag) {
          tag = document.createElement("span");
          tag.className = "pdp-color-soldout-tag";
          tag.textContent = " — sold out";
          title.insertAdjacentElement("afterend", tag);
        } else if (!fully && tag) {
          tag.remove();
        }
      });
    }

    // FEATURE (jul 3, r2): color rail — one hero thumb per color, image-only,
    // pinned left of the grid. Clicking a thumb clicks that color's swatch, so
    // the variant picker + gallery filter behave exactly like a swatch tap.
    var railEl = null;
    function syncRail(color) {
      if (!railEl) return;
      [].slice.call(railEl.querySelectorAll(".pdp-color-rail__thumb")).forEach(function (b) {
        b.classList.toggle("is-active", b.getAttribute("data-rail-color") === color);
      });
    }
    (function buildColorRail() {
      // CLS FIX (aug 18): the rail column is now reserved server-side by
      // pdp-gallery-grid.liquid. Fill the existing placeholder rather than
      // inserting a node, so the hero photo never shifts after first paint.
      var wrap = grid.closest("[data-pdp-grid-wrap]");
      var existing = wrap && wrap.querySelector(".pdp-color-rail");
      // Combined listings (aug 25): a rail that arrives from the server WITH
      // children is the sibling-link rail from pdp-color-rail-cl.liquid — it is
      // final. Do NOT rebuild it and do NOT drop it (colorMedia has <2 keys on
      // combined pages, which used to delete it). railEl stays null so
      // syncRail() never strips its server-set is-active state.
      if (existing && existing.children.length) return;
      function dropReserved() {
        if (wrap) wrap.classList.remove("has-color-rail");
        if (existing) existing.remove();
      }
      if (Object.keys(colorMedia).length < 2) return dropReserved();
      if (!wrap) return;
      var map = byId();
      var rail = existing || document.createElement("div");
      rail.className = "pdp-color-rail";
      Object.keys(colorMedia).forEach(function (color) {
        var first = (colorMedia[color] || []).map(numId).filter(function (id) { return map[id]; })[0];
        var img = first && map[first].querySelector("img");
        if (!img) return;
        var input = [].slice.call(document.querySelectorAll('fieldset.product-form__swatch input[type="radio"]'))
          .filter(function (x) { return x.value === color; })[0];
        var lbl = input && document.querySelector('label[for="' + input.id + '"]');
        var soldout = input && input.closest(".product-form__swatch__item--sold-out");
        var b = document.createElement("button");
        b.type = "button";
        b.className = "pdp-color-rail__thumb" + (soldout ? " is-soldout" : "");
        b.setAttribute("data-rail-color", color);
        b.setAttribute("aria-label", color);
        var i = document.createElement("img");
        i.src = (img.getAttribute("src") || "").replace(/width=\d+/, "width=200");
        i.alt = color;
        i.loading = "lazy";
        b.appendChild(i);
        if (lbl) b.addEventListener("click", function () { lbl.click(); });
        rail.appendChild(b);
      });
      if (!rail.children.length) { dropReserved(); return; }
      wrap.classList.add("has-color-rail");
      if (!existing) wrap.insertBefore(rail, wrap.firstChild);
      railEl = rail;
      log("color rail built:", rail.children.length, "colors");
    })();

    // FEATURE (jul 6): short-gallery text reflow. If EVERY variant shows 2 or
    // fewer images (per-color groups; ungrouped variants count all images),
    // move Description/Specs/Shipping below the gallery so the left column
    // fills with content instead of whitespace. Any variant over 2 images =
    // normal layout, so the layout never jumps between color switches.
    (function moveTextBelowShortGallery() {
      if (!window.matchMedia("(min-width: 981px)").matches) return;
      var totalCells = cells().length;
      if (!totalCells) return;
      var maxVisible = 0;
      if (hasColorMap) {
        var vids = Object.keys(variantOptions);
        if (!vids.length) maxVisible = totalCells;
        vids.forEach(function (vid) {
          var c = colorForVariant(vid);
          var n = (c && colorMedia[c]) ? colorMedia[c].length : totalCells;
          if (n > maxVisible) maxVisible = n;
        });
      } else {
        maxVisible = totalCells;
      }
      if (maxVisible === 0 || maxVisible > 2) return;
      var media = document.querySelector(".product-single__media");
      var col = document.querySelector(".product-single__primary-blocks");
      if (!media || !col) return;
      var movers = [];
      var desc = col.querySelector(".product-single__box__block--description");
      if (desc) movers.push(desc);
      [].slice.call(col.querySelectorAll(".product-single__box__block--tab")).forEach(function (b) { movers.push(b); });
      if (!movers.length) return;
      var wrapEl = document.createElement("div");
      wrapEl.className = "pdp-under-text";
      movers.forEach(function (b) { wrapEl.appendChild(b); });
      media.appendChild(wrapEl);
      log("short gallery (max", maxVisible, "imgs/variant): text moved below images");
    })();

    var last = readVariantId(form);
    if (last) applyVariant(last);
    updateSoldOutTags();

    function check(src) {
      var vid = readVariantId(form);
      if (!vid || vid === last) return;
      log("variant changed via", src, last, "->", vid);
      last = vid;
      applyVariant(vid);
      setTimeout(updateSoldOutTags, 60);
    }
    setInterval(function () { check("poll"); }, POLL_MS);
    form.addEventListener("change", function () { check("change"); }, true);
    form.addEventListener("input", function () { check("input"); }, true);
    document.addEventListener("variant:change", function () { check("variant:change"); });
    document.addEventListener("variant:changed", function () { check("variant:changed"); });
    document.addEventListener("productVariantChange", function () { check("productVariantChange"); });
    document.addEventListener("click", function (e) {
      var t = e.target;
      if (t && t.closest && (t.closest("[data-option-name]") || t.closest('label[for^="ProductSelect"]') ||
          t.closest(".swatch__label") || t.closest(".variant-swatch") || t.closest('input[name^="options"]') ||
          t.closest(".product-form__option"))) {
        setTimeout(function () { check("click"); }, 50);
      }
    });
  }

  function readVariantId(form) {
    var checked = form.querySelector('input[name="id"]:checked');
    if (checked && checked.value) return checked.value;
    var select = form.querySelector('select[name="id"]');
    if (select && select.value) return select.value;
    var hidden = form.querySelector('input[name="id"]');
    if (hidden && hidden.value) return hidden.value;
    return null;
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
